import JSCookie from 'js-cookie';

import { type CookieConfig, type CookieConfigOpt, COOKIES, type CookieConsent } from '@/types/cookies.types';

import { updatePosthogCapturing } from './posthog';

import type { PostHog } from 'posthog-js';

/**
 * Saves user cookie preferences (functional and analytics) into browser cookies.
 * @param {CookieConsent} preferences The user's cookie preferences.
 */
export function saveCookiePreferences({ allowFunctionalCookies, allowAnalyticsCookies }: CookieConsent): void {
  JSCookie.set(COOKIES.ANALYTICS, JSON.stringify(allowAnalyticsCookies));
  JSCookie.set(COOKIES.FUNCTIONAL, JSON.stringify(allowFunctionalCookies));
}

/**
 * Updates and persists the user's cookie consent configuration in localStorage,
 * then returns the generated configuration.
 * @param {CookieConsent} preferences The user's cookie preferences.
 * @returns {CookieConfig} The generated consent configuration.
 */
export function updateConsentConfig({ allowAnalyticsCookies, allowFunctionalCookies }: CookieConsent): CookieConfig {
  const config = {
    instructions: [
      allowAnalyticsCookies
        ? ('rememberCookieConsentGiven' as CookieConfigOpt)
        : ('forgetCookieConsentGiven' as CookieConfigOpt),
      allowFunctionalCookies ? ('rememberConsentGiven' as CookieConfigOpt) : ('forgetConsentGiven' as CookieConfigOpt),
    ],
  };

  localStorage.setItem('geoFluxus', JSON.stringify(config));
  return config;
}

/** Persistence logic based on local storage instructions */
export function getPersistenceSetting():
  'memory' | 'localStorage' | 'cookie' | 'localStorage+cookie' | 'sessionStorage' {
  if (typeof window === 'undefined') return 'memory';
  const instructions = JSON.parse(localStorage.getItem('geoFluxus') || '{}')?.['instructions'];
  return instructions?.[1] === 'forgetCookieConsentGiven' ? 'memory' : 'localStorage+cookie';
}

/** Retrieves the user's cookie consent preferences from localStorage */
export function getCachedCookieConsent(): CookieConsent {
  const instructions =
    typeof window !== 'undefined' ? JSON.parse(localStorage?.getItem('geoFluxus') || '{}')?.['instructions'] : [];

  return {
    allowAnalyticsCookies: instructions?.find((cached: CookieConfigOpt) => cached === 'rememberCookieConsentGiven'),
    allowFunctionalCookies: true,
  };
}

/**
 * Handles cookie consent updates by:
 * 1. Generating tracking configuration based on user consent.
 * 2. Updating PostHog analytics capturing settings.
 *
 * @param consentSettings - User's cookie consent preferences.
 * @param posthog - PostHog analytics instance.
 * @param pushInstruction - Function to send instructions to Matomo.
 */
export function handleCookieConsent(consentSettings: CookieConsent, posthog: PostHog) {
  updateConsentConfig(consentSettings);
  updatePosthogCapturing(consentSettings, posthog);
}
